import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { WhatsappLogo, PaperPlaneTilt, Key } from "@phosphor-icons/react";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}

const STATUS_STYLES = {
  sent: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  accepted: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  declined: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

/* Send WhatsApp template job offers to suppliers and track accept/decline replies. */
export const WhatsAppJobOffers = ({ jobId, vendors = [] }) => {
  const [offers, setOffers] = useState([]);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [selected, setSelected] = useState(null);
  const [params, setParams] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [sending, setSending] = useState(false);
  const [loadErr, setLoadErr] = useState("");
  const [access, setAccess] = useState(null);
  const [includeAccess, setIncludeAccess] = useState(true);
  const [showAccess, setShowAccess] = useState(false);

  const fetchOffers = useCallback(() => {
    API.get(`/whatsapp/jobs/${jobId}/offers`).then(({ data }) => setOffers(data)).catch(() => {});
  }, [jobId]);

  useEffect(() => {
    fetchOffers();
    const iv = setInterval(fetchOffers, 20000);
    return () => clearInterval(iv);
  }, [fetchOffers]);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setParams([]);
    setSupplierId("");
    setLoadErr("");
    setTemplates(null);
    setIncludeAccess(true);
    setShowAccess(false);
    API.get(`/whatsapp/jobs/${jobId}/access-preview`).then(({ data }) => setAccess(data)).catch(() => setAccess({ available: false }));
    API.get("/whatsapp/status").then(({ data }) => {
      setStatus(data);
      if (data.connected) {
        API.get("/whatsapp/templates")
          .then((r) => setTemplates(r.data))
          .catch((e) => setLoadErr(e?.response?.data?.detail || "Could not load templates"));
      }
    }).catch(() => setStatus({ connected: false }));
  }, [open]);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await API.post(`/whatsapp/jobs/${jobId}/offer`, {
        supplier_id: supplierId, name: selected.name, language: selected.language, body_params: params,
        include_access: includeAccess,
      });
      toast.success(data?.access_info === "sent" ? "Job offer + access notes sent via WhatsApp"
        : data?.access_info === "pending" ? "Job offer sent — access notes will follow automatically when they accept"
        : "Job offer sent via WhatsApp");
      setOpen(false);
      fetchOffers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send offer");
    } finally { setSending(false); }
  };

  const withPhone = vendors.filter((v) => (v.phone || "").trim());
  const approved = (templates || []).filter((t) => t.status === "APPROVED");

  return (
    <div data-testid="wa-job-offers">
      <div className="flex items-center justify-between mb-2.5">
        <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
          <WhatsappLogo size={12} weight="fill" /> WhatsApp Job Offers
        </h3>
        <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-700 dark:text-emerald-400 border-emerald-300"
                onClick={() => setOpen(true)} data-testid="wa-offer-btn">
          <PaperPlaneTilt size={12} className="mr-1" /> Send Offer
        </Button>
      </div>
      {offers.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">No offers sent yet — offer this job to a supplier over WhatsApp and they can accept with one tap.</p>
      ) : (
        <div className="space-y-1.5">
          {offers.map((o) => (
            <div key={o.id} className="flex items-center justify-between gap-2 rounded-md border border-border px-2.5 py-1.5" data-testid={`wa-offer-row-${o.id}`}>
              <div className="min-w-0">
                <p className="text-xs font-medium truncate">{o.supplier_name}</p>
                <p className="text-[10px] text-muted-foreground truncate">
                  {o.template} · {timeAgo(o.sent_at)}{o.response_text ? ` · "${o.response_text}"` : ""}
                </p>
                {o.access_info_sent_at ? (
                  <p className="text-[9px] text-emerald-700 dark:text-emerald-400 flex items-center gap-0.5" data-testid={`wa-offer-access-sent-${o.id}`}><Key size={9} /> Access notes sent</p>
                ) : o.access_info_pending ? (
                  <p className="text-[9px] text-amber-700 dark:text-amber-400 flex items-center gap-0.5" data-testid={`wa-offer-access-pending-${o.id}`}><Key size={9} /> Access notes will send on accept</p>
                ) : null}
              </div>
              <Badge className={`text-[9px] border-0 shrink-0 ${STATUS_STYLES[o.status] || ""}`}>{o.status}</Badge>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="wa-offer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <WhatsappLogo className="h-5 w-5 text-emerald-600" weight="fill" /> Offer this job via WhatsApp
            </DialogTitle>
            <DialogDescription>
              Sends an approved template with your quick-reply buttons — the supplier's Accept/Decline reply updates this job automatically.
            </DialogDescription>
          </DialogHeader>
          {status && !status.connected ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
              WhatsApp isn't connected yet — set it up in the <Link to="/whatsapp" className="underline font-medium">WhatsApp Inbox</Link> first.
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Supplier</Label>
                <Select value={supplierId} onValueChange={setSupplierId}>
                  <SelectTrigger className="mt-1" data-testid="wa-offer-supplier-select">
                    <SelectValue placeholder="Choose a supplier with a phone number…" />
                  </SelectTrigger>
                  <SelectContent>
                    {withPhone.map((v) => (
                      <SelectItem key={v.id} value={v.id} data-testid={`wa-offer-supplier-${v.id}`}>
                        {v.company_name || v.contact_name} — {v.phone}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {withPhone.length === 0 && <p className="text-[10px] text-muted-foreground mt-1">No suppliers with a phone number on file.</p>}
              </div>
              {loadErr && <p className="text-sm text-red-500">{loadErr}</p>}
              {!selected ? (
                <div className="max-h-48 overflow-y-auto divide-y divide-border border border-border rounded-md">
                  {templates === null && !loadErr && <p className="p-3 text-xs text-muted-foreground">Loading templates…</p>}
                  {approved.map((t) => (
                    <button key={`${t.name}-${t.language}`} onClick={() => { setSelected(t); setParams(Array(t.param_count).fill("")); }}
                            className="w-full text-left px-3 py-2 hover:bg-muted" data-testid={`wa-offer-template-${t.name}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{t.name}</span>
                        <Badge variant="outline" className="text-[10px]">{t.language}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{t.body}</p>
                    </button>
                  ))}
                  {templates !== null && approved.length === 0 && (
                    <p className="p-4 text-xs text-muted-foreground">
                      No approved templates yet. Tip: create a "job_offer" template in Meta's WhatsApp Manager with Accept / Decline quick-reply buttons.
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="rounded-md border border-border bg-muted/40 p-3">
                    <p className="text-sm font-medium">{selected.name}</p>
                    <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{selected.body}</p>
                  </div>
                  {params.map((p, i) => (
                    <div key={i}>
                      <Label className="text-xs">Value for {"{{" + (i + 1) + "}}"}</Label>
                      <Input className="mt-1" value={p} data-testid={`wa-offer-param-${i}`}
                             onChange={(e) => setParams(params.map((x, j) => (j === i ? e.target.value : x)))} />
                    </div>
                  ))}
                  <div className={`rounded-md border p-3 ${access?.available ? "border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30" : "border-border bg-muted/30"}`} data-testid="wa-offer-access-box">
                    <div className="flex items-start gap-2">
                      <Checkbox id="wa-include-access" checked={includeAccess && !!access?.available} disabled={!access?.available}
                                onCheckedChange={(v) => setIncludeAccess(!!v)} data-testid="wa-offer-include-access" />
                      <div className="flex-1 min-w-0">
                        <label htmlFor="wa-include-access" className="text-xs font-medium flex items-center gap-1 cursor-pointer"><Key size={12} className="text-amber-600" /> Also send access notes &amp; site contact</label>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          {access?.available
                            ? "Free-text can only go inside WhatsApp's 24-hour chat window — it's sent immediately if the supplier has messaged recently, otherwise automatically the moment they accept."
                            : "No access notes or site contact recorded for this property yet."}
                        </p>
                        {access?.available && (
                          <button type="button" onClick={() => setShowAccess((s) => !s)} className="text-[10px] text-primary hover:underline mt-1" data-testid="wa-offer-access-preview-toggle">{showAccess ? "Hide preview" : "Preview message"}</button>
                        )}
                        {showAccess && access?.message && <pre className="mt-2 text-[10px] whitespace-pre-wrap font-sans bg-card border border-border rounded p-2 max-h-40 overflow-y-auto" data-testid="wa-offer-access-preview">{access.message}</pre>}
                      </div>
                    </div>
                  </div>
                  <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => setSelected(null)}>Back</Button>
                    <Button onClick={send} disabled={sending || !supplierId || params.some((p) => !p.trim())}
                            className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-offer-send">
                      {sending ? "Sending…" : "Send Offer"}
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
